import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  Button,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { loadQuestions, Domain, Capability, QuestionData } from '../services/questionService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import CloudIcon from '@mui/icons-material/Cloud';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBack from '@mui/icons-material/ArrowBack';
import './Domains.css';

type DomainIcon = React.ElementType;

interface ExtendedDomain extends Domain {
  selected: boolean;
  iconColor: string;
}

const iconComponents: Record<string, DomainIcon> = {
  'AnalyticsIcon': AnalyticsIcon,
  'TrendingUpIcon': TrendingUpIcon,
  'SpeedIcon': SpeedIcon,
  'SettingsSuggestIcon': SettingsSuggestIcon,
};

const domainColors: Record<string, string> = {
  'Understand Cost & Usage': '#2196f3',
  'Quantify Business Value': '#4caf50',
  'Optimize Usage & Cost': '#ff9800',
  'FinOps Practice': '#9c27b0',
};

const capabilityIcons: Record<string, React.ElementType> = {
  'Data Ingestion': CloudIcon,
  'Allocation': AccountTreeIcon,
  'Reporting & Analytics': AssessmentOutlinedIcon,
  'Budgeting': ReceiptIcon,
  'Forecasting': TimelineIcon,
  'Unit Economics': TrendingUpIcon,
  'Architecting for Cloud': BuildIcon,
  'Rate Optimization': SpeedIcon,
  'Workload Optimization': SettingsSuggestIcon,
  'FinOps Education & Enablement': SchoolIcon,
  'FinOps Practice Operations': SettingsSuggestIcon,
  'FinOps Tools & Services': BuildIcon,
  'Intersecting Disciplines': AccountTreeIcon,
  'Invoicing & Chargeback': ReceiptIcon,
  'Onboarding Workloads': CloudIcon,
};

const audienceIcons: Record<string, React.ElementType> = {
  'Engineering': BuildIcon,
  'Finance': BusinessIcon,
  'FinOps Practitioners': PsychologyIcon,
  'Product Team': InventoryIcon,
  'Leadership': BusinessIcon,
};

const Domains: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [domains, setDomains] = useState<ExtendedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>([]);
  const [availableAudiences, setAvailableAudiences] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;

    const loadDomainQuestions = async () => {
      try {
        setLoading(true);
        const assessmentData = await loadQuestions();

        if (!mounted) return;

        if (!assessmentData?.domains) {
          throw new Error('No domains data received');
        }

        const transformedDomains = assessmentData.domains.map(domain => ({
          ...domain,
          selected: true,
          iconColor: domainColors[domain.name] || '#9c27b0',
        }));

        setDomains(transformedDomains);
        setAvailableAudiences(assessmentData.audiences || []);
        setSelectedAudiences(assessmentData.audiences || []);
        setError(null);
      } catch (error) {
        console.error('Failed to load questions:', error);
        if (mounted) {
          setError('Failed to load domains. Please try again later.');
          setDomains([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDomainQuestions();

    return () => {
      mounted = false;
    };
  }, []);

  const getIconComponent = (iconName: string): DomainIcon => {
    return iconComponents[iconName] || SettingsSuggestIcon;
  };

  const handleDomainSelect = (domainIndex: number) => {
    setDomains(prevDomains => {
      if (!prevDomains || prevDomains.length === 0) return prevDomains;

      const newDomains = [...prevDomains];
      newDomains[domainIndex] = {
        ...newDomains[domainIndex],
        selected: !newDomains[domainIndex].selected,
      };
      return newDomains;
    });
  };

  const handleAudienceChange = (audience: string) => {
    setSelectedAudiences(prev => {
      if (prev.includes(audience)) {
        return prev.filter(a => a !== audience);
      } else {
        return [...prev, audience];
      }
    });
  };

  const getFilteredQuestions = (domain: Domain, capability: Capability) => {
    if (!capability?.questions) return [];
    if (selectedAudiences.length === 0) return capability.questions;

    return capability.questions.filter(question =>
      question?.audiences?.some(audience => selectedAudiences.includes(audience))
    ).map(question => ({
      ...question,
      capability: {
        name: capability.name,
        id: capability.id,
      },
      domain: {
        name: domain.name,
        id: domain.id,
      }
    }));
  };

  const getQuestionCountForAudience = (audience: string) => {
    if (!domains) return 0;
    return domains.reduce((count, domain) => {
      if (!domain?.capabilities) return count;
      return count + domain.capabilities.reduce((capCount, capability) => {
        if (!capability?.questions) return capCount;
        return capCount + capability.questions.filter(q =>
          q?.audiences?.includes(audience)
        ).length;
      }, 0);
    }, 0);
  };

  const getSelectedQuestionCount = () => {
    if (!domains || domains.length === 0) return 0;

    return domains.reduce((count, domain) => {
      if (domain.selected && domain.capabilities) {
        return count + domain.capabilities.reduce((capCount, capability) => {
          const filteredQuestions = getFilteredQuestions(domain,capability);
          return capCount + (filteredQuestions?.length || 0);
        }, 0);
      }
      return count;
    }, 0);
  };

  const handleStartAssessment = () => {
    const selectedQuestions: QuestionData[] = domains
      .filter(domain => domain.selected)
      .flatMap(domain =>
        domain.capabilities
          .flatMap(capability =>
            getFilteredQuestions(domain, capability)
          )
      );

    if (selectedQuestions.length === 0) {
      setError('Please select at least one domain and audience to start the assessment');
      return;
    }

    navigate('/questionnaire', {
      state: {
        questions: selectedQuestions,
        audiences: selectedAudiences
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!domains || domains.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>No domains available</Typography>
      </Box>
    );
  }

  return (
    <Box className="domains-container">
      <Box className="domains-header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h3"
            className="domains-title"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: '-0.5px',
              lineHeight: 1.2,
              color: 'text.primary',
              mb: 0
            }}
          >
            Domains & Capabilities
          </Typography>
        </Box>
      </Box>

      {/* Audience Filter Panel */}
      <Card
        sx={{
          mb: 4,
          backgroundColor: 'background.paper',
          boxShadow: theme.shadows[1],
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">
              Filter by Audience
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the audiences to filter questions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            {availableAudiences.map((audience) => {
              const AudienceIcon = audienceIcons[audience] || PeopleIcon;
              return (
                <Button
                  key={audience}
                  variant={selectedAudiences.includes(audience) ? "contained" : "outlined"}
                  onClick={() => handleAudienceChange(audience)}
                  sx={{
                    p: 2,
                    height: '64px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    textTransform: 'none',
                    borderRadius: 2,
                    backgroundColor: selectedAudiences.includes(audience)
                      ? 'rgba(76, 175, 80, 0.1)'
                      : 'rgba(0, 0, 0, 0.04)',
                    borderColor: selectedAudiences.includes(audience)
                      ? 'rgba(76, 175, 80, 0.3)'
                      : 'rgba(0, 0, 0, 0.1)',
                    color: selectedAudiences.includes(audience)
                      ? 'success.main'
                      : 'text.secondary',
                    '&:hover': {
                      backgroundColor: selectedAudiences.includes(audience)
                        ? 'rgba(76, 175, 80, 0.15)'
                        : 'rgba(0, 0, 0, 0.06)',
                      borderColor: selectedAudiences.includes(audience)
                        ? 'rgba(76, 175, 80, 0.4)'
                        : 'rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <AudienceIcon
                      sx={{
                        fontSize: 20,
                        color: selectedAudiences.includes(audience)
                          ? 'success.main'
                          : 'text.secondary'
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: selectedAudiences.includes(audience) ? 600 : 400,
                        color: selectedAudiences.includes(audience)
                          ? 'success.main'
                          : 'text.secondary',
                      }}
                    >
                      {audience}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={getQuestionCountForAudience(audience)}
                    sx={{
                      backgroundColor: selectedAudiences.includes(audience)
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'rgba(0, 0, 0, 0.08)',
                      color: selectedAudiences.includes(audience)
                        ? 'success.main'
                        : 'text.secondary',
                      height: '20px',
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem',
                      },
                    }}
                  />
                </Button>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Domains Grid */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        '& > *': {
          flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' },
          minWidth: { xs: '100%', md: 'calc(50% - 16px)' },
          height: '100%'
        }
      }}>
        {domains.map((domain, domainIndex) => (
          <Card
            key={domain.name}
            className="domain-card"
            style={{ '--domain-icon-color': domain.iconColor } as React.CSSProperties}
          >
            <CardContent className="domain-card-content">
              <Box className="domain-header">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box className="domain-icon-container">
                    {React.createElement(getIconComponent(domain.icon), { sx: { fontSize: 28 } })}
                  </Box>
                  <Typography variant="h5" className="domain-title">
                    {domain.name}
                  </Typography>
                </Box>
                <Button
                  variant={domain.selected ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => handleDomainSelect(domainIndex)}
                  sx={{
                    minWidth: '40px',
                    height: '40px',
                    padding: '8px',
                    borderRadius: '20px',
                    '&.MuiButton-contained': {
                      backgroundColor: domain.iconColor,
                      '&:hover': {
                        backgroundColor: `${domain.iconColor}dd`,
                      }
                    },
                    '&.MuiButton-outlined': {
                      borderColor: domain.iconColor,
                      color: domain.iconColor,
                      '&:hover': {
                        borderColor: `${domain.iconColor}dd`,
                        backgroundColor: `${domain.iconColor}08`,
                      }
                    }
                  }}
                >
                  {domain.selected ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                </Button>
              </Box>

              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Typography variant="body1" color="text.secondary" className="domain-description">
                  {domain.description}
                </Typography>

                <Button
                  variant="outlined"
                  href={domain.documentation_link}
                  target={domain.documentation_link}
                  rel="noopener noreferrer"
                  className="learn-more-button"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                >
                  Learn More
                </Button>

                <Box className="capabilities-section">
                  <Typography variant="subtitle1" gutterBottom className="capabilities-title">
                    Capabilities
                  </Typography>
                  <Box className="capabilities-list">
                    {domain.capabilities?.map((capability) => {
                      const CapabilityIcon = capabilityIcons[capability.name] || SettingsSuggestIcon;
                      const filteredQuestions = getFilteredQuestions(domain, capability);
                      const hasQuestions = filteredQuestions.length > 0;

                      return (
                        <Chip
                          key={capability.name}
                          icon={<CapabilityIcon sx={{ fontSize: 16 }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{capability.name}</span>
                              {hasQuestions && (
                                <Chip
                                  size="small"
                                  label={filteredQuestions.length}
                                  sx={{
                                    ml: 1,
                                    height: 20,
                                    backgroundColor: domain.selected ? `${domain.iconColor}15` : 'transparent',
                                    color: domain.selected ? domain.iconColor : 'text.secondary',
                                    borderColor: domain.iconColor,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          size="medium"
                          variant={domain.selected && hasQuestions ? "filled" : "outlined"}
                          className="capability-chip"
                          sx={{
                            backgroundColor: domain.selected && hasQuestions ? `${domain.iconColor}15` : 'transparent',
                            color: domain.selected && hasQuestions ? domain.iconColor : 'text.secondary',
                            borderColor: domain.iconColor,
                            '&:hover': {
                              backgroundColor: domain.selected && hasQuestions ? `${domain.iconColor}22` : `${domain.iconColor}11`,
                            },
                            '& .MuiChip-icon': {
                              color: domain.selected && hasQuestions ? domain.iconColor : 'text.secondary',
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Selection Summary */}
      <Box className="selection-summary">
        <Divider className="summary-divider" />

        <Card className="summary-card">
          <Typography variant="h6" gutterBottom className="summary-title">
            Selected Domains and Capabilities
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {domains.map((domain) => (
              <Box
                key={domain.name}
                className="domain-summary"
                style={{
                  '--domain-bg-color': domain.selected ? `${domain.iconColor}08` : 'transparent',
                  '--domain-border-color': domain.selected ? domain.iconColor : 'divider',
                  '--domain-icon-bg-color': `${domain.iconColor}15`,
                  '--domain-icon-color': domain.iconColor
                } as React.CSSProperties}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box className="domain-summary-icon">
                    {React.createElement(getIconComponent(domain.icon), { sx: { fontSize: 20 } })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" className="domain-summary-name">
                      {domain.name}
                    </Typography>
                    <Box className="domain-summary-capabilities">
                      {domain.capabilities?.map((capability) => (
                        <Chip
                          key={capability.name}
                          label={capability.name}
                          size="small"
                          variant={domain.selected ? "filled" : "outlined"}
                          className="capability-summary-chip"
                          sx={{
                            backgroundColor: domain.selected ? `${domain.iconColor}15` : 'transparent',
                            color: domain.selected ? domain.iconColor : 'text.secondary',
                            borderColor: domain.iconColor
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="body2"
                    color={domain.selected ? 'primary' : 'text.secondary'}
                    sx={{ fontWeight: 500 }}
                  >
                    {domain.capabilities?.reduce((count, capability) => {
                      const filteredQuestions = getFilteredQuestions(domain, capability);
                      return count + (filteredQuestions?.length || 0);
                    }, 0) || 0} questions
                  </Typography>
                  <Chip
                    label={domain.selected ? "Selected" : "Not Selected"}
                    size="small"
                    color={domain.selected ? "primary" : "default"}
                    variant={domain.selected ? "filled" : "outlined"}
                    sx={{
                      backgroundColor: domain.selected ? `${domain.iconColor}15` : 'transparent',
                      color: domain.selected ? domain.iconColor : 'text.secondary',
                      borderColor: domain.iconColor
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Card>

        <Box className="start-assessment-container">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon color="primary" />
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
              {getSelectedQuestionCount()} questions selected
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={getSelectedQuestionCount() === 0}
            onClick={handleStartAssessment}
            className="start-assessment-button"
            startIcon={<AssessmentIcon />}
          >
            Start Assessment
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Domains; 